import React, { Fragment, useCallback, useEffect } from 'react'
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { PhotovoltaicTrackerModel } from '../../models/photovoltaic'
import { StringFields, TrackerFields } from './fields'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Checkbox } from 'src/Libs/inputs/formik'
import { useFormikContext } from 'formik'
import { PhotovoltaicFormData } from 'src/Libs/installation-statement/configurations/photovoltaic'
import usePrevious from '@e3dc-react/shell/Hooks/usePrevious'
import { deepEqual } from 'fast-equals'

interface Props {
  tracker: PhotovoltaicTrackerModel
  trackerIndex: number
  push: (obj: unknown) => void
}

/**
 * Form for all photovoltaic data
 * Only fields of the currently selected tab/tracker are shown
 */
const PhotovoltaicForm: React.FC<Props> = ({ tracker, trackerIndex, push }) => {
  const { t } = useCustomTranslation()
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { setValues, values } = useFormikContext<PhotovoltaicFormData>()
  const firstString = usePrevious(values.pvTrackers[trackerIndex].pvStrings[0])

  /**
   * This useEffect is used to update any other string when "Applied to all strings"-checkbox is ticked.
   * Goal of it was to fix the step-validation when multiple strings are selected.
   * related ticket -> PB-427
   */
  useEffect(() => {
    if (tracker.appliedToAllString && !deepEqual(firstString, values.pvTrackers[trackerIndex].pvStrings[0])) {
      applyToAllStringsOnChangeCallback(tracker.appliedToAllString)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.pvTrackers, firstString, tracker.appliedToAllString, trackerIndex])

  const duplicateTracker = (): void => {
    const trackerToCopy = values.pvTrackers[trackerIndex]
    if (!trackerToCopy) return
    push(trackerToCopy)
  }

  /**
   * Apply the same values of string 1 to all other strings of a tracker
   */
  const applyToAllStringsOnChangeCallback = useCallback(
    (value: boolean) => {
      const newValues = {
        ...values,
        pvTrackers: values.pvTrackers.map((tracker, index) =>
          index === trackerIndex
            ? {
                ...tracker,
                appliedToAllString: value,
                pvStrings: tracker.pvStrings.map((_, __, allStrings) => allStrings[0]),
              }
            : { ...tracker }
        ),
      }
      setValues(newValues)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, trackerIndex]
  )

  /**
   * Display the strings of a tracker
   * Rule: A tracker can have 1-3 strings. The number is determined by the string count.
   */
  const getDisplayStrings = (): React.ReactElement => {
    return (
      <Stack spacing={2} sx={{ pt: 4 }} direction={{ xs: 'column', md: 'row' }}>
        {tracker.pvStrings.map((_, stringIndex) => {
          const disableString = stringIndex > 0 && tracker.appliedToAllString
          const hasDivider = stringIndex < tracker.pvStrings.length - 1

          return (
            <Fragment key={stringIndex}>
              <Box sx={{ flex: 1 }} key={`${tracker.name}-string-${stringIndex}`}>
                <Typography variant="h5">{`${t('installation-statement.photovoltaic.form.string.title')} ${stringIndex + 1}`}</Typography>
                <StringFields trackerIndex={trackerIndex} stringIndex={stringIndex} disabled={disableString} />
                {stringIndex === 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Checkbox
                      label={t('installation-statement.photovoltaic.form.tracker.apply-to-all-string')}
                      name={`pvTrackers.[${trackerIndex}].appliedToAllString`}
                      onChange={applyToAllStringsOnChangeCallback}
                    />
                  </Box>
                )}
              </Box>
              {hasDivider && (
                <Divider orientation={isDesktop ? 'vertical' : 'horizontal'} flexItem sx={{ borderColor: theme.palette.shades.shade03 }} />
              )}
            </Fragment>
          )
        })}
      </Stack>
    )
  }

  return (
    <Box sx={{ paddingTop: { xs: 4, sm: 4 } }}>
      <TrackerFields
        index={trackerIndex}
        label={t(`${t('installation-statement.photovoltaic.tab.title')} ${trackerIndex + 1}`)}
        numberOfStrings={(tracker.pvStrings || []).length}
        duplicateTracker={duplicateTracker}
      />
      {getDisplayStrings()}
    </Box>
  )
}

export default PhotovoltaicForm
